import { useCallback, useState } from "react";
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import { extractErrorMessage } from "../../api/client";
import {
  deleteWorkflowOutput,
  patchOutputRetention,
  WorkflowDetail,
} from "../../api/workflows";

// Surfaces the output-retention state for export workflows + lets the
// owner extend the TTL. Hidden for:
//
// - Non-export kinds (no managed retention exists)
// - Pre-v10 export workflows (output_expires_at is null — "unmanaged",
//   the reaper never touches them so there's nothing to extend)
// - Already-deleted outputs (output_deleted_at is set)
//
// The user's view of the truth is the same shape as on search-env
// cards: a chip showing relative TTL, severity color graduating from
// outlined-default → warning → error as expiry approaches.

interface Props {
  wf: WorkflowDetail;
  // Parent's setter so an Extend success re-renders the whole page
  // with the new expires_at without a separate poll round-trip.
  onUpdated: (wf: WorkflowDetail) => void;
}

// Match formatTtl from searchEnvs.ts so users see a consistent
// "expires in 5d 12h" shape across env + retention chips.
function formatTtl(ms: number): string {
  if (ms <= 0) return "expired";
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

function ttlChipColor(
  ms: number,
): "default" | "warning" | "error" {
  if (ms < 6 * 3600 * 1000) return "error"; // <6h
  if (ms < 48 * 3600 * 1000) return "warning"; // <2d
  return "default";
}

export function OutputRetentionPanel({ wf, onUpdated }: Props) {
  const [extending, setExtending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExtend = useCallback(
    async (hours: number) => {
      setExtending(true);
      setError(null);
      try {
        const resp = await patchOutputRetention(wf.id, hours);
        onUpdated(resp);
      } catch (err) {
        setError(extractErrorMessage(err));
      } finally {
        setExtending(false);
      }
    },
    [wf.id, onUpdated],
  );

  const handleForceDelete = useCallback(async () => {
    let prefix = "(prefix not in row)";
    try {
      prefix = JSON.parse(wf.params_json).output ?? prefix;
    } catch {
      // Malformed params_json shouldn't happen — fall through to the
      // confirm dialog with the placeholder so the user still gates.
    }
    if (
      !window.confirm(
        `Force-delete every object under the output prefix?\n\n${prefix}\n\nThis can't be undone.`,
      )
    ) {
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await deleteWorkflowOutput(wf.id);
      // Re-fetch isn't necessary — output_deleted_at will be set on
      // the next poll tick. For an immediate signal, hand the parent
      // a shallow copy with deleted_at populated so the panel flips
      // to the reaped-view immediately.
      onUpdated({
        ...wf,
        output_deleted_at: Date.now(),
      });
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  }, [wf, onUpdated]);

  // Gate: only show for managed export workflows whose output hasn't
  // been deleted by the reaper yet. ``output_expires_at`` is the
  // source of truth — null = unmanaged, surface nothing.
  if (!wf.output_expires_at) return null;
  if (wf.output_deleted_at) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Output retention
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Output was reaped on{" "}
          {new Date(wf.output_deleted_at).toLocaleString()}.
        </Typography>
      </Paper>
    );
  }

  const remainingMs = wf.output_expires_at - Date.now();
  const color = ttlChipColor(remainingMs);
  const expiresAt = new Date(wf.output_expires_at).toLocaleString();

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        rowGap={1}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Output retention
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Typography variant="body2">Expires in</Typography>
            <Chip
              size="small"
              color={color}
              variant={color === "default" ? "outlined" : "filled"}
              label={formatTtl(remainingMs)}
            />
            <Typography variant="caption" color="text.secondary">
              ({expiresAt})
            </Typography>
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Extend by:
          </Typography>
          {[24, 72, 168, 24 * 30, 24 * 90].map((h) => (
            <Button
              key={h}
              size="small"
              variant="text"
              disabled={extending || deleting}
              onClick={() => handleExtend(h)}
            >
              {h < 24 * 7
                ? `${h / 24}d`
                : h === 168
                  ? "7d"
                  : `${h / 24}d`}
            </Button>
          ))}
          <Button
            size="small"
            color="error"
            variant="text"
            disabled={extending || deleting}
            onClick={handleForceDelete}
          >
            Force delete
          </Button>
        </Stack>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        Operator-managed: when expiry passes, the output prefix on S3
        will be reaped automatically. Extension is additive, clamped
        at 30 days from submission.
      </Typography>
    </Paper>
  );
}
